import { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  ToggleButton, ToggleButtonGroup, FormControl, InputLabel, Select, MenuItem, TextField,
  FormControlLabel, Checkbox, Link, CircularProgress, Tooltip,
} from "@mui/material";
import useClientPaging from "../../components/admin/useClientPaging";
import api from "../../api";

const BASE = "/admin/question-bank/school-practice-papers";
const muted = "#4b5563";
const ok = "#1b5e20";

const BATCH = {
  batch1: { bg: "#e8f5e9", color: "#1b5e20" },
  batch2: { bg: "#e3f2fd", color: "#0d47a1" },
  batch3: { bg: "#f3e8ff", color: "#5b21b6" },
};
const STATUS = {
  live: { label: "Live", bg: "#e8f5e9", color: "#1b5e20" },
  pending: { label: "Pending", bg: "#fff3e0", color: "#9a4a00" },
  rejected: { label: "Rejected", bg: "#ffebee", color: "#b71c1c" },
  retired: { label: "Retired", bg: "#f3f4f6", color: "#374151" },
};
const REASON = {
  old_format: { label: "Old format — 2027 edition live", bg: "#fff3e0", color: "#9a4a00" },
  duplicate: { label: "Same questions as a full test", bg: "#f3f4f6", color: "#374151" },
  other: { label: "Retired", bg: "#f3f4f6", color: "#374151" },
};

function Chip({ bg, color, children }) {
  return (
    <Box component="span" sx={{ display: "inline-block", borderRadius: 2.5, px: 1.1, py: 0.3, fontSize: 12, fontWeight: 700, bgcolor: bg, color, whiteSpace: "nowrap" }}>
      {children}
    </Box>
  );
}

/** One load for both tabs (the server caches it for 10 minutes). */
export function useReleases() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    api.get(`${BASE}/releases`)
      .then((r) => live && setData(r.data))
      .catch((e) => live && setError(e?.response?.data?.message || "Could not load the release list."));
    return () => { live = false; };
  }, []);
  return { data, error };
}

const adminUrl = (type, id) => (type === "mock" ? `/admin/mock-exam/mock/${id}/report` : `/admin/question-bank/papers/${id}`);

export function ReleasesTab({ data, error }) {
  const [batch, setBatch] = useState("all");
  const [type, setType] = useState("");
  const [school, setSchool] = useState("");
  const [status, setStatus] = useState("");
  const [changedOnly, setChangedOnly] = useState(false);
  const [search, setSearch] = useState("");
  const items = useMemo(() => data?.items ?? [], [data]);
  const batches = data?.batches ?? [];
  const schools = useMemo(() => [...new Set(items.map((r) => r.school).filter(Boolean))].sort(), [items]);
  const counts = useMemo(() => items.reduce((c, r) => ({ ...c, [r.batch]: (c[r.batch] || 0) + 1 }), {}), [items]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((r) =>
      (batch === "all" || r.batch === batch) && (!type || r.type === type) && (!school || r.school === school) &&
      (!status || r.status === status) && (!changedOnly || r.changes.length > 0) &&
      (!q || r.name.toLowerCase().includes(q) || (r.school || "").toLowerCase().includes(q)));
  }, [items, batch, type, school, status, changedOnly, search]);
  const { pageRows, pager } = useClientPaging(rows);

  if (error) return <Typography sx={{ color: "#b71c1c", fontSize: 14 }}>{error}</Typography>;
  if (!data) return <Box sx={{ display: "flex", gap: 1, alignItems: "center", color: muted }}><CircularProgress size={18} /> Loading the releases…</Box>;

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center" }}>
        <ToggleButtonGroup size="small" exclusive value={batch} onChange={(_, v) => v && setBatch(v)} aria-label="Batch">
          <ToggleButton value="all">All · {items.length}</ToggleButton>
          {batches.map((b) => <ToggleButton key={b.key} value={b.key}>{b.label} · {b.date} · {counts[b.key] || 0}</ToggleButton>)}
        </ToggleButtonGroup>
        <TextField size="small" label="Search" placeholder="Paper or school" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: "1 1 220px", bgcolor: "#fff" }} />
        <FormControl size="small" sx={{ minWidth: 170, bgcolor: "#fff" }}>
          <InputLabel id="rel-type">Type</InputLabel>
          <Select labelId="rel-type" label="Type" value={type} onChange={(e) => setType(e.target.value)}>
            <MenuItem value="">Practice papers &amp; mocks</MenuItem>
            <MenuItem value="paper">Practice papers</MenuItem>
            <MenuItem value="mock">Mock exams</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200, bgcolor: "#fff" }}>
          <InputLabel id="rel-school">School</InputLabel>
          <Select labelId="rel-school" label="School" value={school} onChange={(e) => setSchool(e.target.value)}>
            <MenuItem value="">All schools</MenuItem>
            {schools.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130, bgcolor: "#fff" }}>
          <InputLabel id="rel-status">Status</InputLabel>
          <Select labelId="rel-status" label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="">Any</MenuItem>
            {Object.entries(STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControlLabel control={<Checkbox size="small" checked={changedOnly} onChange={(e) => setChangedOnly(e.target.checked)} />} label="Changed only" />
      </Box>
      <Typography sx={{ fontSize: 13, color: muted }}>
        Every practice paper and mock exam of the format-match releases. <b>What changed</b> is the release note plus what has happened since: questions fixed by the QA bot, and questions a tester has edited or verified (those are never overwritten by a sync).
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5 }}>
        <Table size="small" sx={{ minWidth: 1080 }}>
          <TableHead sx={{ bgcolor: "#f9fafb" }}>
            <TableRow>
              {["Batch", "Type", "Name", "School / test", "Qs", "Status", "Signed off", "What changed"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", color: "#374151" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((r) => (
              <TableRow key={`${r.type}-${r.id}`} hover sx={{ verticalAlign: "top" }}>
                <TableCell><Chip {...(BATCH[r.batch] || BATCH.batch1)}>{r.batch_label}</Chip></TableCell>
                <TableCell sx={{ fontSize: 13 }}>{r.type === "mock" ? "Mock exam" : "Practice paper"}</TableCell>
                <TableCell sx={{ fontSize: 13, maxWidth: 300 }}>
                  <Link href={adminUrl(r.type, r.id)} underline="hover">{r.name}</Link>{" "}
                  <Box component="span" sx={{ color: muted }}>#{r.id}</Box>
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>{r.school || "—"}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{r.questions}</TableCell>
                <TableCell><Chip {...(STATUS[r.status] || STATUS.pending)}>{(STATUS[r.status] || { label: r.status }).label}</Chip></TableCell>
                <TableCell sx={{ fontSize: 13, color: r.signed_off ? ok : muted, fontWeight: 600 }}>{r.signed_off ? "Yes" : "Not yet"}</TableCell>
                <TableCell sx={{ fontSize: 13, minWidth: 260, whiteSpace: "normal" }}>
                  {r.changes.length ? r.changes.map((c) => <Box key={c}>{c}</Box>) : <Box component="span" sx={{ color: muted }}>No change since it went live</Box>}
                </TableCell>
              </TableRow>
            ))}
            {!pageRows.length && (
              <TableRow><TableCell colSpan={8} sx={{ color: muted, fontSize: 13 }}>Nothing matches these filters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pager}
    </>
  );
}

export function HiddenTab({ data, error }) {
  const [reason, setReason] = useState("all");
  const hidden = useMemo(() => data?.hidden ?? [], [data]);
  const counts = useMemo(() => hidden.reduce((c, r) => ({ ...c, [r.reason]: (c[r.reason] || 0) + 1 }), {}), [hidden]);
  const rows = useMemo(() => hidden.filter((r) => reason === "all" || r.reason === reason), [hidden, reason]);
  const { pageRows, pager } = useClientPaging(rows);

  if (error) return <Typography sx={{ color: "#b71c1c", fontSize: 14 }}>{error}</Typography>;
  if (!data) return <Box sx={{ display: "flex", gap: 1, alignItems: "center", color: muted }}><CircularProgress size={18} /> Loading…</Box>;

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center" }}>
        <ToggleButtonGroup size="small" exclusive value={reason} onChange={(_, v) => v && setReason(v)} aria-label="Why hidden">
          <ToggleButton value="all">All · {hidden.length}</ToggleButton>
          <ToggleButton value="old_format">Old format, 2027 edition live · {counts.old_format || 0}</ToggleButton>
          <ToggleButton value="duplicate">Same questions as a full test · {counts.duplicate || 0}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Typography sx={{ fontSize: 13, color: muted }}>
        Retired papers are off the shop and out of school bundles, on purpose. Whoever already has one keeps it (bought on its own or in a pack).
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5 }}>
        <Table size="small" sx={{ minWidth: 1100 }}>
          <TableHead sx={{ bgcolor: "#f9fafb" }}>
            <TableRow>
              {["ID", "Paper", "Why hidden", "Live instead", "Signed off", "Holders"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", color: "#374151" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((r) => (
              <TableRow key={r.id} hover sx={{ verticalAlign: "top" }}>
                <TableCell sx={{ fontSize: 13, color: muted }}>#{r.id}</TableCell>
                <TableCell sx={{ fontSize: 13 }}><Link href={adminUrl("paper", r.id)} underline="hover">{r.name}</Link></TableCell>
                <TableCell><Chip {...(REASON[r.reason] || REASON.other)}>{(REASON[r.reason] || REASON.other).label}</Chip></TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  {r.instead.length ? r.instead.map((x) => (
                    <Box key={x.id}><Link href={adminUrl("paper", x.id)} underline="hover">#{x.id} {x.name}</Link></Box>
                  )) : (r.reason === "old_format" ? "Not in the 2027 test" : "—")}
                </TableCell>
                <TableCell sx={{ fontSize: 13, color: r.signed_off ? ok : muted, fontWeight: 600 }}>{r.signed_off ? "Yes" : "No"}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  <Tooltip title="People who already have it keep access"><span>{r.holders ? `${r.holders} ${r.holders === 1 ? "holder keeps" : "holders keep"} access` : "None"}</span></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pager}
    </>
  );
}
